import { createSlice } from '@reduxjs/toolkit'
import { AppDispatch, AppGetState } from 'domains/application/store'
import { User } from 'domains/organization/data-types'

export interface Card {
  name: string
  number: string
  id: string
}

export interface Vehicle {
  name: string
  status: 'active' | 'sold' | 'out_of_service' | 'in_shop' | 'unknown'
  vin: string | null
  make: string | null
  model: string | null
  operator_id: string
  operator?: User
  card?: Card
}

export interface State {
  vehicles: Vehicle[]
  usersById: Record<User['id'], User>
  userStatesById: Record<User['id'], { fetching: boolean }>
  fetching: boolean
}

const slice = createSlice({
  name: 'application',
  initialState: {
    vehicles: [],
    fetching: false,
    usersById: {},
    userStatesById: {},
  } as State,
  reducers: {
    fetchStart: (state: State, _action: { payload: Record<never, never> }) => {
      state.fetching = true
    },
    fetchSuccess: (
      state: State,
      action: { payload: { vehicles: Vehicle[] } },
    ) => {
      state.vehicles = action.payload.vehicles
      state.vehicles.forEach((v) => {
        if (!v.operator) {
          return
        }
        state.usersById[v.operator.id] = v.operator
      })
      state.fetching = false
    },
    fetchUsers: (
      state: State,
      action: {
        payload: {
          userIds: User['id'][]
        }
      },
    ) => {
      action.payload.userIds.forEach((id) => {
        const userState = state.userStatesById[id] ?? {}
        state.userStatesById = {
          ...state.userStatesById,
          [id]: { ...userState, fetching: true },
        }
      })
    },
    fetchUsersSuccess: (
      state: State,
      action: {
        payload: {
          users: User[]
        }
      },
    ) => {
      action.payload.users.forEach((user) => {
        const id = user.id
        const userState = state.userStatesById[id] ?? {}
        state.userStatesById = {
          ...state.userStatesById,
          [id]: { ...userState, fetching: false },
        }
        state.usersById[id] = user
      })
    },
  },
})

export const fetchVehicles =
  () => async (dispatch: AppDispatch, getState: AppGetState) => {
    dispatch(slice.actions.fetchStart({}))
    const { data: vehicles } = await (await fetch('/api/vehicles')).json()
    dispatch(slice.actions.fetchSuccess({ vehicles }))

    const state = getState().vehicle
    const userIds = state.vehicles.map((vehicle) => vehicle.operator_id)

    if (userIds.length > 0) {
      dispatch(slice.actions.fetchUsers({ userIds }))
      const { data: users } = await (await fetch('/api/users')).json()
      dispatch(slice.actions.fetchUsersSuccess({ users }))
    }
  }

export default slice
